import React, { useRef } from 'react';
import { Caption, CaptionProps, DayPicker, DayProps, useDayRender } from 'react-day-picker-v8';
import { t } from '@lingui/macro';
import type { Locale } from 'date-fns';
import { format } from 'date-fns';

import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import { Box, Flex, Text } from '@rover/kibble/core';
import type { ThemeTextColor } from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';

import 'react-day-picker-v8/dist/style.css';

import { getDateFnsLocale } from './utils';

enum DayTypes {
  DEFAULT,
  AVAILABLE,
  NOT_AVAILABLE,
}

export type dayTypeProps = {
  style: React.ComponentProps<typeof Box>['sx'];
  textColor: ThemeTextColor;
  ariaLabel: string;
};

export type Props = {
  language?: string;
  startDate?: Date;
  fromMonth?: Date;
  toMonth?: Date;
  availableDates?: Date[];
  notAvailableDates?: Date[];
  onMonthChange?: (date: Date) => void;
  isEU?: boolean;
  numberOfMonths?: number;
};

type customDayProps = DayProps & {
  isOneMonth?: boolean;
};

const CustomDay = ({ isOneMonth, ...props }: customDayProps): JSX.Element => {
  const { background, colors } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);

  const width = isOneMonth ? ['100%', '44px', '40px'] : '100%';

  const isOutsideMonth = props.displayMonth.getMonth() !== props.date.getMonth();

  let dayType = DayTypes.DEFAULT;
  if (dayRender.activeModifiers.available) dayType = DayTypes.AVAILABLE;
  if (dayRender.activeModifiers.notAvailable) dayType = DayTypes.NOT_AVAILABLE;
  if (dayRender.isHidden) dayType = DayTypes.DEFAULT;

  const propsByDayType: Record<string, dayTypeProps> = {
    [DayTypes.DEFAULT]: {
      style: {
        backgroundColor: background.primary,
      },
      textColor: 'tertiary',
      ariaLabel: '',
    },
    [DayTypes.NOT_AVAILABLE]: {
      style: {
        background: `repeating-linear-gradient(135deg, ${background.primary},${background.primary} 5px, #E7E8EB 5px,#E7E8EB 10px)`,
        textShadow: `1.5px 1.5px 0 ${background.primary}, -1.5px 1.5px 0 ${background.primary}, -1.5px -1.5px 0 ${background.primary}, 1.5px -1.5px 0 ${background.primary}, 0px 1.5px 0 ${background.primary}, 0px -1.5px 0 ${background.primary}, 1.5px 0px 0 ${background.primary}, -1.5px 0px 0 ${background.primary};`,
      },
      textColor: 'tertiary',
      ariaLabel: t`Not available`,
    },
    [DayTypes.AVAILABLE]: {
      style: {
        backgroundColor: colors.green['200'],
      },
      textColor: 'primary',
      ariaLabel: t`Available`,
    },
  };

  return (
    <Box aria-hidden={isOutsideMonth || undefined} sx={{ padding: '2px' }}>
      <Box
        webProps={{
          tabIndex: dayRender.isHidden ? -1 : 0,
        }}
        aria-label={propsByDayType[dayType].ariaLabel}
        aria-hidden={isOutsideMonth || undefined}
        display="flex"
        width={width}
        height="44px"
        borderRadius="8px"
        justifyContent="center"
        sx={propsByDayType[dayType].style}
      >
        {!dayRender.isHidden && (
          <Box
            display="flex"
            width={width}
            height="44px"
            borderRadius="8px"
            justifyContent="center"
            alignContent="center"
          >
            <Box display="flex" alignSelf="center">
              <Text size="100" fontWeight="semibold" textColor={propsByDayType[dayType].textColor}>
                {dayRender.buttonProps.children}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const RenderCustomCaption = (isOneMonth: boolean = true): JSX.Element => {
  const { colors, background } = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="row"
      width="100%"
      sx={{
        paddingLeft: isOneMonth ? '7px' : '0x',
        paddingBottom: isOneMonth ? '12px' : '8x',
        paddingTop: isOneMonth ? '8px' : '0x',
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        width={isOneMonth ? '50%' : 'auto'}
        alignItems="center"
      >
        <Box
          borderRadius="4px"
          width="16px"
          height="16px"
          sx={{ backgroundColor: colors.green['200'] }}
        />
        <Text ml="2x">{t`Available`}</Text>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        width={isOneMonth ? '50%' : 'auto'}
        alignItems="center"
        ml={isOneMonth ? '0x' : '4x'}
      >
        <Box
          borderRadius="4px"
          width="16px"
          height="16px"
          sx={{
            background: `repeating-linear-gradient(135deg, ${background.primary},${background.primary} 2.5px, #E7E8EB 2.5px,#E7E8EB 5px)`,
          }}
        />
        <Text ml="2x">{t`Not available`}</Text>
      </Box>
    </Box>
  );
};

const CustomCaption = (props: CaptionProps): JSX.Element => {
  return (
    <>
      <Caption {...props} />
      {RenderCustomCaption()}
    </>
  );
};

const BookingsCalendar = ({ numberOfMonths = 1, ...props }: Props): JSX.Element => {
  const { text, textColor, heading } = useTheme();
  const dateFnsLocale = getDateFnsLocale(props.language || DEFAULT_LANG);
  const isOneMonth = numberOfMonths === 1;

  const formatWeekdayName = (weekday: Date, options?: { locale?: Locale }): string => {
    return format(weekday, 'EEEEE', options);
  };

  const modifiers = {
    ...(props.availableDates && { available: props.availableDates }),
    ...(props.notAvailableDates && { notAvailable: props.notAvailableDates }),
  };

  const startMonth = props.startDate ? props.startDate : new Date();

  const renderCustomDay = (dayProps: DayProps): JSX.Element => (
    <CustomDay {...dayProps} isOneMonth={isOneMonth} />
  );

  return (
    <Flex flexDirection="column" width="100%">
      {!isOneMonth && RenderCustomCaption(isOneMonth)}
      <DayPicker
        locale={dateFnsLocale}
        mode="single"
        defaultMonth={startMonth}
        fromMonth={props.fromMonth || startMonth}
        toMonth={props.toMonth}
        numberOfMonths={numberOfMonths}
        ISOWeek={props.isEU}
        styles={{
          root: { margin: '0px', width: '100%' },
          months: { width: '100%', gap: '10px' },
          multiple_months: { width: '100%' },
          month: {
            width: isOneMonth ? '100%' : 'inherit',
            margin: isOneMonth ? 'inherit' : '0px',
            justifyContent: isOneMonth ? 'inherit' : 'center',
          },
          table: { width: '100%', maxWidth: 'initial' },
          head_cell: {
            fontSize: text['100'].fontSize,
            lineHeight: text['100'].lineHeight,
            color: textColor.tertiary,
          },
          caption_label: {
            fontSize: heading['300'].fontSize,
            lineHeight: heading['100'].lineHeight,
            color: textColor.primary,
            padding: '0px',
          },
        }}
        components={{
          Day: renderCustomDay,
          Caption: isOneMonth ? CustomCaption : undefined,
        }}
        modifiers={modifiers}
        formatters={{ formatWeekdayName }}
        onMonthChange={props.onMonthChange}
      />
    </Flex>
  );
};

export default BookingsCalendar;
